import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ChangePasswordPayload,
  ForgotPayload,
  loginOtp,
  ResetPasswordPayload,
  User,
  UserLogin,
  UserSignUp,
  ValidateAccount,
  VerifyOtp
} from '../models/auth.model';
import { CommonResponse } from '../models/common.model';
import { vendorCompleteSignUpPayload, VendorSignUp } from '../models/vendor.model';
import { Document } from '../models/documents.model';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { isPlatformBrowser } from '@angular/common';
import { notificationListing } from '../models/notificationListing.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  deviceTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(
    'test'
  );
  private menuState = new BehaviorSubject<boolean>(false);

  profileDetails: BehaviorSubject<User | null> =
    new BehaviorSubject<User | null>(null);

  resetTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  menuState$ = this.menuState.asObservable();

  cartCount: BehaviorSubject<number | null> =
    new BehaviorSubject<number | null>(null);

  membershipStatus: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  private isOpenSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private _translateService: TranslateService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    // Initialize membership status from localStorage
    if (isPlatformBrowser(this.platformId)) {
      const status = localStorage.getItem('membershipStatus');
      this.membershipStatus.next(status === 'true');
    }
  }

  toggleSidebarView(): void {
    this.isOpenSubject.next(!this.isOpenSubject.value);
  }

  toggleMenu(): void {
    this.menuState.next(!this.menuState.value);
  }

  closeMenu(): void {
    this.menuState.next(false);
  }

  openMenu(): void {
    this.menuState.next(true);
  }

  isAuthenticated(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      if (
        localStorage.getItem(environment.storageKey) &&
        localStorage.getItem(environment.storageKey) !== null
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  updateMembershipStatus(status: boolean): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('membershipStatus', status.toString());
      this.membershipStatus.next(status);
    }
  }

  getMembershipStatus(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const status = localStorage.getItem('membershipStatus');
      return status === 'true';
    }
    return false;
  }

  userSignUp(payload: UserSignUp): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/signup`,
      payload
    );
  }

  sendOtp(payload: ForgotPayload): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/sendOtp`,
      payload
    );
  }

  verifyOtp(payload: VerifyOtp, queryString: string): Observable<User> {
    return this.http
      .post<{
        data: User;
      }>(`${environment.base_url}user/verifyOtp?${queryString}`, payload)
      .pipe(map(res => res.data));
  }


  loginOtp(payload: loginOtp, queryString: string): Observable<User> {
    return this.http
      .post<{
        data: User;
      }>(`${environment.base_url}user/verifyOtp?${queryString}`, payload)
      .pipe(map(res => res.data));
  }

  updateProfile(payload: User): Observable<User> {
    return this.http
      .put<{ data: User }>(`${environment.base_url}user/updateProfile`, payload)
      .pipe(map(res => res.data));
  }

  getProfile(): Observable<User> {
    return this.http
      .get<{ data: User }>(`${environment.base_url}user/getProfile`)
      .pipe(map(res => res.data));
  }

  validateAccount(payload: UserSignUp): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/validateAccount`,
      payload
    );
  }

  verifyValidate(payload: ValidateAccount): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/verifyValidate`,
      payload
    );
  }

  userLogin(payload: UserLogin, queryString: string): Observable<User> {
    return this.http
      .post<{
        data: User;
        message?: string;
      }>(`${environment.base_url}user/login?${queryString}`, payload)
      .pipe(map(res => {
        // For trial flow, attach the message to the user object
        const user = res.data;
        if (res.message) {
          (user as any).message = res.message;
        }
        return user;
      }));
  }

  forgotPassword(payload: UserSignUp): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/forgotPassword`,
      payload
    );
  }

  verifyForgotPassword(payload: ValidateAccount): Observable<User> {
    return this.http
      .post<{
        data: User;
      }>(`${environment.base_url}user/verifyForgotPass`, payload)
      .pipe(map(res => res.data));
  }

  resetPassword(data: ResetPasswordPayload): Observable<CommonResponse> {
    return this.http
      .post<CommonResponse>(`${environment.base_url}user/resetPassword`, data)
      .pipe(map(res => res));
  }

  logout(): Observable<CommonResponse> {
    return this.http.get<CommonResponse>(`${environment.base_url}user/logout`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logoutAlert(): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Promise((resolve: any) => {
      Swal.fire({
        title: this._translateService.instant('AreYouSure'),
        text: this._translateService.instant('Youwillbeloggedout'),
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: this._translateService.instant('Cancel'),
        confirmButtonColor: '#ffaf18',
        cancelButtonColor: '#000',
        confirmButtonText: this._translateService.instant('Logout')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then((result: any) => {
        return resolve(result);
      });
    });
  }

  vendorSignUp(payload: VendorSignUp | null): Observable<VendorSignUp> {
    return this.http.post<{data:VendorSignUp}>(
      `${environment.base_url}user/vendorSignUp`,
      payload
    ).pipe(map(res => res.data));
  }

  vendorSignUpStep2(payload: vendorCompleteSignUpPayload | null): Observable<CommonResponse> {
    return this.http.post<CommonResponse>(
      `${environment.base_url}user/updateVendorProfile`,
      payload
    );
  }

  getDocuments(): Observable<Document[]> {
    return this.http
      .get<{ data: Document[] }>(`${environment.base_url}user/requiredDocument`)
      .pipe(map(res => res.data));
  }

  changePassword(data: ChangePasswordPayload): Observable<CommonResponse> {
    return this.http
      .post<CommonResponse>(`${environment.base_url}user/changePassword`, data)
      .pipe(map(res => res));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  passwordChangedLogout(): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Promise((resolve: any) => {
      Swal.fire({
        title: this._translateService.instant('PasswordUpdatedSuccessfully'),
        text: this._translateService.instant('Youwillbeloggedout'),
        icon: 'success'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }).then((result: any) => {
        return resolve(result);
      });
    });
  }

  getNotificationsList(query?: string): Observable<notificationListing> {
    return this.http
      .get<{ data: notificationListing }>(`${environment.base_url}user/getNotifications?${query}`)
      .pipe(map(res => res.data));
  }
}
